import { useNavigate } from 'react-router-dom';

import { CellContext, ColumnDef } from '@tanstack/react-table';
import useSubscriptions from 'api/useSubscriptions';
import {
  BillingPeriodUnit,
  Subscription,
  SubscriptionStatus
} from 'common/contracts';
import { EPermissionAction, EPermissionGroup } from 'common/permissions.types';
import { DATE_TIME_FORMAT_SHORTER } from 'constants/constants';
import dayjs from 'dayjs';
import Decimal from 'decimal.js';
import { capitalizeFirstLetter } from 'utils/formattingUtil';
import { permissionsUtil } from 'utils/permissionsUtil';
import { EToasterStatus, showToast } from 'utils/showToast';

import Chip from 'components/Chip/Chip';
import ActionCell from 'components/DataTableComponent/components/ActionCell';

import { BILLING_PERIOD_DISPLAY_NAME, statusMapping } from '../constants';

export function useSubscriptionsColumns() {
  const navigate = useNavigate();
  const { cancelSubscription } = useSubscriptions();

  const columns: ColumnDef<Subscription, unknown>[] = [
    { accessorKey: '_id', header: 'Subscription ID' },
    { accessorKey: 'customerId', header: 'Player ID' },
    {
      accessorKey: 'createdAt',
      header: 'Subscribed Since',
      cell: (cell: CellContext<any, unknown>) => {
        const subscribedSince = cell.getValue() as string;
        return subscribedSince
          ? dayjs(subscribedSince).utc().format(DATE_TIME_FORMAT_SHORTER)
          : '';
      }
    },
    { accessorKey: 'customerEmail', header: 'Player Email' },
    {
      accessorKey: 'billingPeriodUnit',
      header: 'Billing Period',
      cell: (cell: CellContext<any, unknown>) => {
        const billingPeriodUnit = cell.getValue() as BillingPeriodUnit;
        return BILLING_PERIOD_DISPLAY_NAME[billingPeriodUnit];
      }
    },
    {
      accessorKey: 'nextBillingDate',
      header: 'Next Billing',
      cell: (cell: CellContext<any, unknown>) => {
        const subscribedSince = cell.getValue() as string;
        return subscribedSince
          ? dayjs(subscribedSince).utc().format(DATE_TIME_FORMAT_SHORTER)
          : '-';
      }
    },
    {
      accessorKey: 'lifeTimeValueInUsd',
      header: 'LTV',
      cell: (cell: CellContext<any, unknown>) => {
        const value = cell.getValue() as number;
        const numberWithFraction = new Decimal(value).div(100).toNumber();
        return `$${numberWithFraction}`;
      }
    },
    { accessorKey: 'successfulBillings', header: 'Successful Billings' },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: (cell: CellContext<Subscription, unknown>) => {
        const status = cell.getValue() as SubscriptionStatus;
        const mappedStatus = statusMapping[status];

        return (
          <Chip variant="flat" status={mappedStatus}>
            {capitalizeFirstLetter(status)}
          </Chip>
        );
      }
    },
    {
      accessorKey: 'actions',
      header: '',
      maxSize: 5,
      size: 5,
      cell: (cell: CellContext<any, unknown>) => {
        const { original } = cell.row;

        const isCancelDisabled =
          original.status !== SubscriptionStatus.ACTIVE &&
          original.status !== SubscriptionStatus.OVERDUE;

        const isActionEnabled = permissionsUtil.isActionEnabled(
          null,
          EPermissionGroup.SUBSCRIPTIONS,
          EPermissionAction.MANAGE,
          original.publisherId
        );

        return (
          <ActionCell
            rowData={original}
            actions={[
              {
                label: 'Cancel',
                disabled: isCancelDisabled || !isActionEnabled,
                handler: () => {
                  cancelSubscription.mutate(
                    {
                      subscriptionId: original._id,
                      publisherId: original.publisherId
                    },
                    {
                      onSuccess: () => {
                        showToast({
                          message: 'Subscription canceled successfully',
                          status: EToasterStatus.SUCCESS
                        });
                      },
                      onError: () => {
                        showToast({
                          message: 'Failed to cancel subscription',
                          status: EToasterStatus.ERROR
                        });
                      }
                    }
                  );
                }
              },
              {
                label: 'Show orders',
                handler: () => {
                  navigate(`/orders?subscriptionId=${original._id}`);
                }
              }
            ]}
          />
        );
      }
    }
  ];

  return { columns };
}
